import cn from 'classnames';
import { forwardRef } from 'react';

import type { ForwardedRef, ReactElement } from 'react';

import type { ListboxGroupProps } from './listbox-group.types';

import './listbox-group.scss';

/**
 * @internal
 * The `ListboxGroup` component is used to display a list of grouped items aligned with design system styles.
 * Use the `ListboxItem` component to define the items of the list and `ListboxGroupLabel` to define the group label.
 *
 * ### Usage
 *
 * import { Listbox, ListboxItem, ListboxGroup, ListboxGroupLabel } from '@bloomreach/react-banana-ui';
 *
 * export default function MyList() {
 *   return (
 *      <Listbox>
 *        <ListboxGroup>
 *          <ListboxGroupLabel>Group one</ListboxGroupLabel>
 *          <ListboxItem>Item 1</ListboxItem>
 *          <ListboxItem>Item 2</ListboxItem>
 *          <ListboxItem>Item 3</ListboxItem>
 *        </ListboxGroup>
 *        <ListboxGroup>
 *          <ListboxGroupLabel>Group two</ListboxGroupLabel>
 *          <ListboxItem>Item 1</ListboxItem>
 *          <ListboxItem>Item 2</ListboxItem>
 *          <ListboxItem>Item 3</ListboxItem>
 *        </ListboxGroup>
 *      </Listbox>
 *   );
 * }
 */
const ListboxGroup = forwardRef(
  (props: ListboxGroupProps, forwardedRef: ForwardedRef<HTMLUListElement> | null): ReactElement => {
    const {
      children,
      className,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const classes = cn('rbui-listbox-group', className);

    return (
      <li>
        <ul className={classes} role="group" {...rest} ref={forwardedRef}>
          {children}
        </ul>
      </li>
    );
  },
);

ListboxGroup.displayName = 'ListboxGroup';

export default ListboxGroup;
